import {
  LinkIcon,
  PhoneIcon,
  EmailIcon,
  ShopIcon,
  ScheduleIcon,
  GetStartedIcon,
  WebsiteIcon,
  HelpIcon,
  AppIcon,
  BuyIcon,
  AccountIcon,
  LearnMoreIcon,
  LocationIcon,
  DemoIcon,
  SignupIcon,
  ServicesIcon
} from '../components/UI/ButtonIcons';

/**
 * Gets emoji icon component from IOS_ICONS_MAP
 * This is used for feedback options (CSAT) to ensure consistent cross-platform rendering
 * @param {string} iconId - The emoji icon ID (e.g., 'heartEyes', 'smilingFace')
 * @returns {React.Component|null} - The corresponding emoji icon component, or null if not found
 */
export const getEmojiIconComponent = (iconId) => {
  // Dynamic import to avoid circular dependencies
  try {
    const { IOS_ICONS_MAP } = require('../../components/Chatbot/Setup/iosIconsMap');
    return IOS_ICONS_MAP[iconId] || null;
  } catch (error) {
    console.warn('Failed to load IOS_ICONS_MAP:', error);
    return null;
  }
};

/**
 * Maps icon string values to their corresponding React icon components
 * @param {string} iconValue - The string value of the icon (e.g., 'phone', 'email')
 * @returns {React.Component} - The corresponding icon component, defaults to LinkIcon if not found
 */
export const getIconComponent = (iconValue) => {
  // Check if it's an emoji icon first
  if (iconValue === 'star') {
    return getEmojiIconComponent('star') || LinkIcon;
  }
  
  const iconMap = {
    'link': LinkIcon,
    'phone': PhoneIcon,
    'email': EmailIcon,
    'shop': ShopIcon,
    'schedule': ScheduleIcon,
    'get-started': GetStartedIcon,
    'website': WebsiteIcon,
    'help': HelpIcon,
    'app': AppIcon,
    'buy': BuyIcon,
    'account': AccountIcon,
    'learn-more': LearnMoreIcon,
    'location': LocationIcon,
    'demo': DemoIcon,
    'signup': SignupIcon,
    'services': ServicesIcon,
  };
  
  return iconMap[iconValue] || LinkIcon; // Default to LinkIcon if not found
};

/**
 * Gets all available icon options for dropdowns/forms
 * @returns {Array} - Array of icon options with value, label, and icon component
 */
export const getIconOptions = () => {
  const StarIcon = getEmojiIconComponent('star');
  
  return [
    { value: 'link', label: 'Link', icon: LinkIcon },
    { value: 'phone', label: 'Phone', icon: PhoneIcon },
    { value: 'email', label: 'Email', icon: EmailIcon },
    { value: 'shop', label: 'Shop', icon: ShopIcon },
    { value: 'schedule', label: 'Schedule', icon: ScheduleIcon },
    { value: 'get-started', label: 'Get Started', icon: GetStartedIcon },
    { value: 'website', label: 'Website', icon: WebsiteIcon },
    { value: 'help', label: 'Help', icon: HelpIcon },
    { value: 'app', label: 'App', icon: AppIcon },
    { value: 'buy', label: 'Buy', icon: BuyIcon },
    { value: 'account', label: 'Account', icon: AccountIcon },
    { value: 'learn-more', label: 'Learn More', icon: LearnMoreIcon },
    { value: 'location', label: 'Location', icon: LocationIcon },
    { value: 'demo', label: 'Demo', icon: DemoIcon },
    { value: 'signup', label: 'Signup', icon: SignupIcon },
    { value: 'services', label: 'Services', icon: ServicesIcon },
    { value: 'star', label: 'Star', icon: StarIcon },
  ];
};

/**
 * Validates if an icon value is supported
 * @param {string} iconValue - The icon value to validate
 * @returns {boolean} - True if the icon value is supported
 */
export const isValidIconValue = (iconValue) => {
  const validValues = [
    'link', 'phone', 'email', 'shop', 'schedule', 'get-started',
    'website', 'help', 'app', 'buy', 'account', 'learn-more',
    'location', 'demo', 'signup', 'services', 'star'
  ];
  return validValues.includes(iconValue);
};
